import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { fetchChangeRequestsForLookup } from '../../services/cabAgendaItemsService';
import { changeStateLabels } from '../../utils/labelMappings';
import type { LookupChangeRequest } from '../../types/cabAgendaItem';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const EMPTY_ITEMS: LookupChangeRequest[] = [];

function formatChangeState(changeState?: string | null): string {
  if (!changeState) return '—';
  return changeStateLabels[changeState] || changeState;
}

function findAutoSelectCandidate(
  items: LookupChangeRequest[],
  searchTerm: string,
  initialSelectedId?: string | null
): LookupChangeRequest | null {
  if (items.length === 0) return null;

  const term = searchTerm.trim().toLowerCase();
  if (term) {
    const exactMatches = items.filter(
      (item) => item.number.trim().toLowerCase() === term
    );
    if (exactMatches.length === 1) return exactMatches[0];
    if (items.length === 1) return items[0];
  }

  if (initialSelectedId) {
    return items.find((item) => item.id === initialSelectedId) ?? null;
  }

  if (items.length === 1) return items[0];
  return null;
}

interface ChangeLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  initialSelectedId?: string | null;
  /** Prefill and run search when the modal opens (e.g. text already typed in Task field). */
  initialSearch?: string;
  onConfirm: (selected: LookupChangeRequest) => void;
}

const ChangeLookupModal: React.FC<ChangeLookupModalProps> = ({
  isOpen,
  onClose,
  title = 'Change Requests',
  initialSelectedId = null,
  initialSearch = '',
  onConfirm,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<LookupChangeRequest | null>(null);
  /** Bumps on each Search so auto-select re-runs even when the term is unchanged. */
  const [searchEpoch, setSearchEpoch] = useState(0);

  // Reset only when the modal opens (capture current initialSearch once)
  useEffect(() => {
    if (!isOpen) return;
    const seed = (initialSearch || '').trim();
    setSearchInput(seed);
    setAppliedSearch(seed);
    setPage(1);
    setSelected(null);
    setSearchEpoch((n) => n + 1);
    // intentionally only when isOpen flips true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['lookup-changes', 'cab-agenda-eligible', page, pageSize, appliedSearch],
    queryFn: () =>
      fetchChangeRequestsForLookup({
        page,
        pageSize,
        search: appliedSearch || undefined,
      }),
    enabled: isOpen,
    staleTime: 15000,
  });

  const items = data?.items ?? EMPTY_ITEMS;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (!isOpen || isFetching || !data) return;

    const candidate = findAutoSelectCandidate(items, appliedSearch, initialSelectedId);
    if (candidate) {
      setSelected(candidate);
    }
  }, [isOpen, isFetching, data, items, appliedSearch, initialSelectedId, searchEpoch]);

  const runSearch = () => {
    setAppliedSearch(searchInput.trim());
    setPage(1);
    setSelected(null);
    setSearchEpoch((n) => n + 1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    // Prevent Enter from submitting the parent Agenda Item <form>
    e.preventDefault();
    e.stopPropagation();
    runSearch();
  };

  const isSelected = useCallback(
    (id: string) => selected?.id === id,
    [selected]
  );

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm(selected);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-4 py-3 border-b border-gray-100">
                  {/* Use div (not nested <form>) so Enter does not submit the Agenda Item page form */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Search by Change Number or Short Description..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={runSearch}
                      className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-auto min-h-[280px]">
                  {isLoading ? (
                    <div className="p-8 text-center text-gray-500 text-sm">
                      Loading change requests...
                    </div>
                  ) : items.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-sm">
                      No change requests found
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 w-10" aria-label="Select" />
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Change Number
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Assigned To
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Assignment Group
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Short Description
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            State
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {items.map((change) => (
                          <tr
                            key={change.id}
                            className={`cursor-pointer hover:bg-indigo-50 ${
                              isSelected(change.id) ? 'bg-indigo-50' : ''
                            }`}
                            onClick={() => setSelected(change)}
                            onDoubleClick={() => {
                              onConfirm(change);
                              onClose();
                            }}
                          >
                            <td className="px-4 py-2">
                              <input
                                type="radio"
                                name="change-lookup-selection"
                                checked={isSelected(change.id)}
                                onChange={() => setSelected(change)}
                                className="h-4 w-4 text-indigo-600 border-gray-300"
                                aria-label={`Select ${change.number}`}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-indigo-700 font-medium whitespace-nowrap">
                              {change.number}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                              {change.assignedToName}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                              {change.assignmentGroupName}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {change.shortDescription || '—'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                {formatChangeState(change.changeState)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="px-4 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Show</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <span>
                      {total === 0
                        ? '0 of 0'
                        : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
                    </span>
                    {isFetching && !isLoading && (
                      <span className="text-xs text-gray-400">Updating...</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1.5 text-sm border rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1.5 text-sm border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={!selected}
                      onClick={handleConfirm}
                      className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Select
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ChangeLookupModal;
